import React, { useEffect, useRef, useState } from "react";
import PeakBrandMark, { CaretText } from "../PeakBrandMark";
import { OnboardingProgressBar } from "../OnboardingStepHeader";
import { OnboardingCircleNextButton } from "../OnboardingButtons";
import { OnboardingAnimatedCheck } from "../OnboardingRows";
import { OnboardingVideoBackdrop, ReadinessTag } from "../OnboardingVideoBackdrop";
import {
  KNOW_THYSELF_CREDITS,
  WELCOME_CREDITS_INDEX,
  WELCOME_HELPS,
  WELCOME_INSIGHT_LEAD,
  WELCOME_PHILOSOPHY,
  WELCOME_SLIDES,
  WELCOME_SOCRATES,
} from "../onboardingCopy";

interface WelcomeStepProps {
  onContinue: () => void;
  onExistingAccount: () => void;
}

/** Auto-scrolling, draggable, gradient-masked list of what PEAK^ models. */
const CreditsRoll: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const [offset, setOffset] = useState(36);
  const contentRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const dragStart = useRef({ y: 0, offset: 36 });

  useEffect(() => {
    setOffset(36);
    if (!isActive) return;
    let frame: number;
    const step = () => {
      if (!dragging.current) {
        setOffset((prev) => {
          const height = contentRef.current?.offsetHeight ?? 1;
          const next = prev - 0.55;
          return next < -height ? 36 : next;
        });
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [isActive]);

  return (
    <div
      className="relative h-[220px] w-full overflow-hidden touch-none select-none"
      style={{
        maskImage: "linear-gradient(to bottom, transparent, #fff 25%, #fff 75%, transparent)",
        WebkitMaskImage: "linear-gradient(to bottom, transparent, #fff 25%, #fff 75%, transparent)",
      }}
      onPointerDown={(e) => {
        dragging.current = true;
        dragStart.current = { y: e.clientY, offset };
      }}
      onPointerMove={(e) => {
        if (!dragging.current) return;
        setOffset(dragStart.current.offset + (e.clientY - dragStart.current.y));
      }}
      onPointerUp={() => { dragging.current = false; }}
      onPointerCancel={() => { dragging.current = false; }}
      onPointerLeave={() => { dragging.current = false; }}
    >
      <div ref={contentRef} className="absolute left-0 right-0" style={{ transform: `translateY(${offset}px)` }}>
        {KNOW_THYSELF_CREDITS.map((line) => (
          <p key={line} className="font-geist text-[13px] leading-[29px] text-white/65">{line}</p>
        ))}
      </div>
    </div>
  );
};

const WelcomeStep: React.FC<WelcomeStepProps> = ({ onContinue, onExistingAccount }) => {
  const [page, setPage] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [checked, setChecked] = useState<boolean[]>(WELCOME_HELPS.map(() => false));
  const touchStartX = useRef<number | null>(null);
  const lastSlide = page === WELCOME_SLIDES.length - 1;

  // Slide 2's checklist springs upright one item at a time (480 / 620 ms stagger).
  useEffect(() => {
    setChecked(WELCOME_HELPS.map(() => false));
    if (page !== 1) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let delay = 0;
    WELCOME_HELPS.forEach((_, i) => {
      delay += i === 0 ? 480 : 620;
      timers.push(setTimeout(() => setChecked((prev) => prev.map((c, j) => (j === i ? true : c))), delay));
    });
    return () => timers.forEach(clearTimeout);
  }, [page]);

  const next = () => (lastSlide ? onContinue() : setPage((p) => p + 1));
  const prev = () => setPage((p) => Math.max(0, p - 1));

  return (
    <div className="relative min-h-[100dvh] w-full overflow-hidden bg-black text-white">
      <OnboardingVideoBackdrop onTime={setSeconds} />

      <div
        className="relative mx-auto flex min-h-[100dvh] w-full max-w-[480px] flex-col px-6 pb-6 pt-4 lg:max-w-[720px] lg:px-10 lg:pb-12 lg:pt-10"
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchStartX.current;
          touchStartX.current = null;
          if (dx < -50 && !lastSlide) setPage((p) => p + 1);
          if (dx > 50) prev();
        }}
      >
        <div className="flex items-center pb-4">
          <PeakBrandMark inverted enlarged />
          <div className="flex-1" />
          <button type="button" onClick={onContinue} className="font-funnel text-[15px] text-white/55">Skip</button>
          <div className="pl-3">
            <OnboardingProgressBar filled={page + 1} total={WELCOME_SLIDES.length} inverted />
          </div>
        </div>

        <div className="pb-5">
          <ReadinessTag seconds={seconds} />
        </div>

        <div className="flex flex-1 flex-col justify-center">
          {page === 0 && (
            <div className="flex flex-col gap-7" key="slide-0">
              <h1 className="font-funnel text-[38px] font-bold leading-[1.02] tracking-[-0.76px] text-white">{WELCOME_SLIDES[0].title}</h1>
              <p className="font-geist text-[15px] text-white/90">{WELCOME_SLIDES[0].body}</p>
            </div>
          )}
          {page === 1 && (
            <div className="flex flex-col gap-7" key="slide-1">
              <div className="flex flex-col gap-1.5">
                <p className="font-geist text-[15px] text-white/85">{WELCOME_PHILOSOPHY}</p>
                <div className="flex items-baseline gap-2.5">
                  <span className="font-caveat text-[44px] leading-none text-white">{WELCOME_SLIDES[1].title}</span>
                  <span className="font-funnel text-[14px] font-light italic text-white/60">{WELCOME_SOCRATES}</span>
                </div>
                <p className="font-geist text-[15px] text-white/90">{WELCOME_INSIGHT_LEAD}</p>
              </div>
              <ul className="flex flex-col gap-[18px] pl-[3px]">
                {WELCOME_HELPS.map((item, i) => (
                  <li key={item} className="flex items-center gap-3.5">
                    <OnboardingAnimatedCheck checked={checked[i]} size={26} inverted />
                    <span className="font-funnel text-[17px] text-white">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {page === WELCOME_CREDITS_INDEX && (
            <div className="flex flex-col gap-4" key="slide-2">
              <h1 className="font-funnel text-[38px] font-bold leading-[1.02] tracking-[-0.76px] text-white">{WELCOME_SLIDES[2].title}</h1>
              <p className="font-funnel text-[17px] text-white/90">
                <CaretText text={WELCOME_SLIDES[2].body} caretClassName="font-groteskLogo font-bold text-[17px] text-peak-brand" />
              </p>
              <CreditsRoll isActive={page === WELCOME_CREDITS_INDEX} />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-6">
          {page === 0 && (
            <button type="button" onClick={onExistingAccount} className="font-funnel text-[14px] text-left text-white">
              Already have an account? <span className="font-semibold">Download the app</span>
            </button>
          )}
          <div className="flex-1" />
          <OnboardingCircleNextButton light onClick={next} />
        </div>
      </div>
    </div>
  );
};

export default WelcomeStep;
